import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils';
import { ProposalStep3AllAttributesDto } from '@gap/backend/app-services/proposal/models/ProposalStep3Dto';
import { PaymentRadioOptionProps } from './ProposalSectionPricingSchemeSelector';
import ProposalsViewProposalStore from '../../ProposalsViewProposalStore';
import { PaymentMethodOptionValue } from '@gap/backend/app-services/services/models/ServiceRateTypesDto';

export default class ProposalSectionPricingSchemeSelectorStore {
   sp

   constructor(sp: ProposalSectionPricingSchemeSelectorStoreProps) {
      this.sp = sp
   }

   get options() {
      const options: PaymentRadioOptionProps[] = []

      if (this.sp.pricingItem.monthlyInstallmentsOver12Months) {
         options.push({
            value: 'monthly-instalments',
            label: 'Monthly instalments over 12-month period',
            disabled: this.shouldOptionBeDisabled('monthly-instalments'),
         })
      }

      if (this.sp.pricingItem.paymentOnCompletion) {
         options.push({
            value: 'on-completion',
            label: 'Payment on completion',
            disabled: this.shouldOptionBeDisabled('on-completion'),
         })
      }

      if (this.sp.pricingItem.annualPayment) {
         options.push({
            value: 'annual',
            label: 'Annual payment',
            ...(this.sp.pricingItem.annualPaymentDiscount ? {
               caption: `Includes ${this.sp.pricingItem.annualPaymentDiscount}% discount`,
            } : {}),
            disabled: this.shouldOptionBeDisabled('annual'),
         })
      }

      if (this.sp.pricingItem.upfrontPaymentInFull) {
         options.push({
            value: 'upfront',
            label: 'Upfront payment',
            ...(this.sp.pricingItem.upfrontPaymentInFullDiscount ? {
               caption: `Includes ${this.sp.pricingItem.upfrontPaymentInFullDiscount}% discount`,
            } : {}),
            disabled: this.shouldOptionBeDisabled('upfront'),
         })
      }

      if (this.sp.pricingItem.stagedPayments) {
         options.push({
            value: 'staged',
            label: 'Staged payments',
            caption: this.stagedPaymentCaption(),
            disabled: this.shouldOptionBeDisabled('staged'),
         })
      }

      return options
   }

   shouldOptionBeDisabled = (method: PaymentMethodOptionValue) => {
      const { paymentPreferences } = this.sp.proposalsViewProposalStore.proposal.response || {}
      return !this.sp.proposalsViewProposalStore.currentSignee ||
         (!!paymentPreferences && paymentPreferences[this.sp.pricingItem.serviceRateType] !== method)
   }

   stagedPaymentCaption = () => {
      if (!this.sp.pricingItem.stagedPayments) {
         return undefined
      }

      const completionPaymentPercent = this.stagedCompletionPayment()

      return [
         `${this.sp.pricingItem.stagedPaymentUpfront}% upfront`,
         `${this.sp.pricingItem.stagedPaymentMilestone}% at designated milestone`,
         ...(completionPaymentPercent ? [`${completionPaymentPercent}% on completion`] : []),
      ].join(', ');
   }

   stagedCompletionPayment = () => {
      return 100 - this.sp.pricingItem.stagedPaymentUpfront - this.sp.pricingItem.stagedPaymentMilestone
   }
}

interface ProposalSectionPricingSchemeSelectorStoreProps {
   pricingItem: ProposalStep3AllAttributesDto,
   proposalsViewProposalStore: ProposalsViewProposalStore,
}

export const { Provider: ProposalSectionPricingSchemeSelectorStoreProvider, useProvidedStore: useProposalSectionPricingSchemeSelectorStore } =
   createStoreContext(ProposalSectionPricingSchemeSelectorStore)
